import { CompanionFormat, CustomCtaCreative } from './CompanionTypes';

export type CompanionPhase = 'idle' | 'adActive' | 'adEnded';
export type CompanionAssets = 'loading' | 'ready' | 'failed';

export interface CompanionSelectionInput {
  phase: CompanionPhase;
  creative?: CustomCtaCreative;
  assets: CompanionAssets;
  formats: CompanionFormat[];
  /** A native (GPT) companion slot has been filled. */
  standardFilled: boolean;
  /** The displayed standard companion was retained after normal completion. */
  standardRetained: boolean;
}

/** The in-house creative may be shown once its icon has loaded, and only while its ad plays. */
function inHouseReady(input: CompanionSelectionInput): boolean {
  return (
    input.phase === 'adActive' && input.assets === 'ready' && !!input.creative && input.formats.includes('customCta')
  );
}

/** Whether the in-house creative has the region reserved, i.e. it is enabled and has not failed to load. */
function inHouseOwnsRegion(input: CompanionSelectionInput): boolean {
  return !!input.creative && input.formats.includes('customCta') && input.assets !== 'failed';
}

/**
 * Decides which format owns the region. The in-house creative wins whenever it can render, and nothing
 * shows while its icon loads. Otherwise a standard companion fills in during the ad, or remains after the ad
 * only when it was explicitly retained.
 */
export function selectFormat(input: CompanionSelectionInput): CompanionFormat | 'none' {
  if (input.phase === 'idle') {
    return 'none';
  }
  if (inHouseReady(input)) {
    return 'customCta';
  }
  if (!input.standardFilled || !input.formats.includes('standard')) {
    return 'none';
  }
  if (input.phase === 'adEnded') {
    return input.standardRetained ? 'standard' : 'none';
  }
  return inHouseOwnsRegion(input) ? 'none' : 'standard';
}

/**
 * Tracks the lifecycle of one ad's companion. Every new ad, clear or release bumps `epoch`,
 * so asynchronous results captured under an older epoch can be dropped.
 */
export class CompanionState {
  phase: CompanionPhase = 'idle';
  epoch = 0;
  creative?: CustomCtaCreative;
  assets: CompanionAssets = 'loading';
  private contentResumed = false;

  start(creative?: CustomCtaCreative): number {
    this.reset();
    this.phase = 'adActive';
    this.creative = creative;
    return ++this.epoch;
  }

  setAssets(assets: CompanionAssets): void {
    this.assets = assets;
  }

  /** The ad finished normally; skips and errors call `clear` instead. */
  end(): void {
    if (this.phase === 'adActive') {
      this.phase = 'adEnded';
    }
  }

  /** The ad break is over and content is playing again. */
  resumeContent(): void {
    this.contentResumed = true;
  }

  get resumed(): boolean {
    return this.contentResumed;
  }

  clear(): void {
    this.reset();
    this.epoch++;
  }

  private reset(): void {
    this.phase = 'idle';
    this.creative = undefined;
    this.assets = 'loading';
    this.contentResumed = false;
  }
}
